import { authResponseSchema, hasAccessToken, unwrap, type InvitationPublicOut, type MeOut } from "@sagot/api-client";
import { CONSOLE_PATHS, LEGAL_LINKS, NAME_MAX_LENGTH, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, PRODUCT_NAME, consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, EmptyState, Field, Input, Skeleton, toast } from "@sagot/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { z } from "zod";
import { useErrorMessage } from "../lib/api-errors";
import { queryKeys } from "../lib/query-keys";
import { zodResolver } from "../lib/zod-resolver";
import { useApi, useClient, useSession } from "../session/session-provider";
import { useMe } from "../session/use-me";
import { AuthDivider } from "./components/auth-divider";
import { AuthLayout } from "./components/auth-layout";
import { FormAlert } from "./components/form-alert";
import { OAuthButton } from "./components/oauth-buttons";
import { PasswordInput } from "./components/password-input";
import { PasswordStrength } from "./components/password-strength";
import { InviteSummary } from "./invite-summary";

function useAcceptSchema() {
  const { t } = useTranslation();
  return z.object({
    name: z.string().trim().min(1, t("auth.common.nameRequired")).max(NAME_MAX_LENGTH),
    title: z.string().trim().max(200),
    password: z.string().min(PASSWORD_MIN_LENGTH, t("auth.password.tooShort")).max(PASSWORD_MAX_LENGTH),
    consent: z.literal(true, { errorMap: () => ({ message: t("auth.register.consentRequired") }) }),
  });
}
type AcceptValues = z.infer<ReturnType<typeof useAcceptSchema>>;

/** After acceptance: adopt the new session (new account) and open the joined workspace. */
function useFinishAccept() {
  const client = useClient();
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation();
  return async (workspaceId: string, auth: unknown, workspaceName: string) => {
    const parsed = authResponseSchema.safeParse(auth);
    if (parsed.success && hasAccessToken(parsed.data)) client.session.setSession(parsed.data);
    const me: MeOut = await queryClient.query({ queryKey: queryKeys.me, queryFn: () => unwrap(api.GET("/v1/me")), staleTime: 0 });
    const slug = me.workspaces.find((w) => w.id === workspaceId)?.slug;
    toast.success(t("auth.invite.joined", { workspace: workspaceName }));
    await navigate(slug ? consoleRoutes.dashboard(slug) : "/", { replace: true });
  };
}

function NewAccountForm({ token, invitation }: { token: string; invitation: InvitationPublicOut }) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const finish = useFinishAccept();
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit, control, formState } = useForm<AcceptValues>({ resolver: zodResolver(useAcceptSchema()), defaultValues: { name: "", title: "", password: "" } });
  const password = useWatch({ control, name: "password" });
  const onSubmit = handleSubmit(async (v) => {
    setError(null);
    try {
      const result = await unwrap(api.POST("/v1/invitations/{token}/accept", { params: { path: { token } }, body: { name: v.name, title: v.title || null, password: v.password, consent: v.consent } }));
      await finish(result.workspace_id, result.auth, invitation.workspace_name);
    } catch (caught) {
      setError(message(caught));
    }
  });
  return (
    <>
      <OAuthButton provider="google" intent="invite" invite={token} label={t("auth.common.continueGoogle")} className="mt-5 w-full" />
      <AuthDivider label={t("auth.invite.orSetPassword")} className="my-5" />
      {error ? <FormAlert title={error} onDismiss={() => { setError(null); }} className="mb-4" /> : null}
      <form className="space-y-4" noValidate onSubmit={(event) => void onSubmit(event)}>
        <Field label={t("auth.register.fullName")} error={formState.errors.name?.message}>
          <Input autoComplete="name" {...register("name")} />
        </Field>
        <Field
          label={
            <>
              {t("auth.invite.jobTitle")} <span className="font-medium text-fg-subtle">{t("auth.invite.optional")}</span>
            </>
          }
        >
          <Input autoComplete="organization-title" {...register("title")} />
        </Field>
        <Field label={t("auth.common.password")} error={formState.errors.password?.message}>
          <PasswordInput placeholder={t("auth.password.placeholder")} autoComplete="new-password" {...register("password")} />
          <PasswordStrength value={password} />
        </Field>
        <label className="flex items-start gap-2.5 text-[13px] text-fg-muted">
          <input type="checkbox" className="checkbox mt-0.5" aria-invalid={formState.errors.consent ? true : undefined} {...register("consent")} />
          <span>
            {t("auth.invite.consentLead")}{" "}
            <a href={LEGAL_LINKS.terms} target="_blank" rel="noreferrer" className="font-semibold text-primary-ink hover:underline">
              {t("auth.common.terms")}
            </a>{" "}
            {t("auth.invite.consentTail")}
          </span>
        </label>
        {formState.errors.consent ? (
          <p role="alert" className="text-xs font-semibold text-danger">
            {formState.errors.consent.message}
          </p>
        ) : null}
        <Button type="submit" size="lg" className="w-full" loading={formState.isSubmitting}>
          {t("auth.invite.submit")}
        </Button>
      </form>
    </>
  );
}

function SignedInAccept({ token, invitation, email }: { token: string; invitation: InvitationPublicOut; email: string }) {
  const { t } = useTranslation();
  const api = useApi();
  const client = useClient();
  const message = useErrorMessage();
  const finish = useFinishAccept();
  const [busy, setBusy] = useState(false);
  if (email.toLowerCase() !== invitation.email.toLowerCase()) {
    return (
      <FormAlert tone="warning" title={t("auth.invite.mismatchTitle")} className="mt-5">
        {t("auth.invite.mismatchBody", { current: email, invited: invitation.email })}{" "}
        <button type="button" className="font-semibold text-primary-ink hover:underline" onClick={() => void client.session.logout()}>
          {t("auth.invite.signOut")}
        </button>
      </FormAlert>
    );
  }
  const accept = async () => {
    setBusy(true);
    try {
      const result = await unwrap(api.POST("/v1/invitations/{token}/accept", { params: { path: { token } }, body: { consent: true } }));
      await finish(result.workspace_id, result.auth, invitation.workspace_name);
    } catch (caught) {
      toast.error(message(caught));
      setBusy(false);
    }
  };
  return (
    <>
      <p className="mt-1 text-fg-muted">{t("auth.invite.signedInAs", { email })}</p>
      <Button size="lg" className="mt-5 w-full" loading={busy} onClick={() => void accept()}>
        {t("auth.invite.acceptSignedIn")}
      </Button>
    </>
  );
}

function DeclineLink({ token, invitation }: { token: string; invitation: InvitationPublicOut }) {
  const { t } = useTranslation();
  const api = useApi();
  const navigate = useNavigate();
  const message = useErrorMessage();
  const decline = async () => {
    try {
      await unwrap(api.POST("/v1/invitations/{token}/decline", { params: { path: { token } } }));
      toast.info(invitation.inviter_name ? t("auth.invite.declined", { inviter: invitation.inviter_name }) : t("auth.invite.declinedNoInviter"));
      await navigate(CONSOLE_PATHS.login, { replace: true });
    } catch (caught) {
      toast.error(message(caught));
    }
  };
  return (
    <p className="mt-5 text-center text-xs text-fg-subtle">
      {t("auth.invite.notYou")}{" "}
      <button type="button" className="font-semibold text-fg-muted hover:text-fg hover:underline" onClick={() => void decline()}>
        {t("auth.invite.decline")}
      </button>
    </p>
  );
}

function InviteBody({ token, invitation }: { token: string; invitation: InvitationPublicOut }) {
  const { t } = useTranslation();
  const session = useSession();
  const me = useMe();
  const signedIn = session.status === "authenticated";
  return (
    <>
      <InviteSummary invitation={invitation} />
      <h1 className="mt-8 text-2xl font-extrabold tracking-tight">{t("auth.invite.setupHeading")}</h1>
      {signedIn && me.data ? (
        <SignedInAccept token={token} invitation={invitation} email={me.data.user.email} />
      ) : (
        <>
          <p className="mt-1 text-fg-muted">
            {t("auth.invite.joiningAs")} <strong className="text-fg">{invitation.email}</strong>.
          </p>
          <NewAccountForm token={token} invitation={invitation} />
        </>
      )}
      <DeclineLink token={token} invitation={invitation} />
    </>
  );
}

export function AcceptInvitePage() {
  const { t } = useTranslation();
  const api = useApi();
  const { token = "" } = useParams();
  const invitation = useQuery({
    queryKey: queryKeys.invitation(token),
    queryFn: () => unwrap(api.GET("/v1/invitations/{token}", { params: { path: { token } } })),
    retry: false,
  });
  const next = `${CONSOLE_PATHS.login}?next=${encodeURIComponent(consoleRoutes.acceptInvite(token))}`;
  return (
    <AuthLayout
      title={t("auth.invite.title", { product: PRODUCT_NAME })}
      prompt={{ text: t("auth.common.alreadyHaveAccount"), label: t("auth.invite.signInToAccept"), href: next }}
      width="md"
      align="start"
    >
      {invitation.isPending ? (
        <div className="space-y-4" aria-busy="true">
          <Skeleton className="h-48 w-full rounded-lg" />
          <Skeleton className="h-8 w-[220px]" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : invitation.isError ? (
        <div className="card">
          <h1 className="sr-only">{t("auth.invite.invalidTitle")}</h1>
          <EmptyState
            icon="mail"
            title={t("auth.invite.invalidTitle")}
            description={t("auth.invite.invalidBody")}
            action={
              <Button asChild variant="secondary">
                <AppLink href={CONSOLE_PATHS.login}>{t("auth.invite.goToSignIn")}</AppLink>
              </Button>
            }
          />
        </div>
      ) : (
        <InviteBody token={token} invitation={invitation.data} />
      )}
    </AuthLayout>
  );
}
